import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggle, className = '' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();

  const navigationSections = [
    {
      title: 'Core Platform',
      items: [
        { name: 'Dashboard', path: '/multi-role-dashboard', icon: 'LayoutDashboard', description: 'Overview & insights' },
        { name: 'Discovery Engine', path: '/program-discovery-engine', icon: 'Search', description: 'Find opportunities' },
        { name: 'Scouting Interface', path: '/real-time-scouting-interface', icon: 'Radar', description: 'Real-time matching' },
      ]
    },
    {
      title: 'Performance',
      items: [
        { name: 'Performance Center', path: '/referral-performance-center', icon: 'TrendingUp', description: 'Track success metrics' },
        { name: 'Analytics Center', path: '/analytics-insights-center', icon: 'BarChart3', description: 'Deep insights' },
      ]
    },
    {
      title: 'Hub',
      items: [
        { name: 'Landing Hub', path: '/unified-landing-hub', icon: 'Home', description: 'Main entry point' },
      ]
    }
  ];

  const quickActions = [
    { name: 'New Match', icon: 'Plus', action: () => console.log('New Match'), variant: 'default' },
    { name: 'Quick Scout', icon: 'Zap', action: () => console.log('Quick Scout'), variant: 'outline' },
  ];

  const isActivePath = (path) => location?.pathname === path;
  const shouldShowExpanded = !isCollapsed || isHovered;

  return (
    <aside 
      className={`fixed left-0 top-16 bottom-0 z-40 bg-card border-r border-border transition-all duration-300 ease-brand ${
        isCollapsed ? 'w-16' : 'w-64'
      } ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col h-full">
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {shouldShowExpanded && (
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-gradient-to-br from-primary to-accent rounded flex items-center justify-center">
                <Icon name="Network" size={14} color="white" />
              </div>
              <span className="font-semibold text-sm text-foreground">Navigation</span>
            </div>
          )}
          
          {onToggle && (
            <button
              onClick={onToggle}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-all duration-200"
            >
              <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
            </button>
          )}
        </div>

        {/* Navigation Sections */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-6">
            {navigationSections?.map((section, sectionIndex) => (
              <div key={section?.title}>
                {shouldShowExpanded && (
                  <div className="px-4 mb-2">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {section?.title}
                    </h3>
                  </div>
                )}
                
                <div className="space-y-1 px-2">
                  {section?.items?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`group flex items-center px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                        isActivePath(item?.path)
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-center w-5 h-5 mr-3">
                        <Icon 
                          name={item?.icon} 
                          size={18} 
                          className={isActivePath(item?.path) ? 'text-primary-foreground' : ''}
                        />
                      </div>
                      
                      {shouldShowExpanded && (
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{item?.name}</div>
                          {item?.description && (
                            <div className="text-xs text-muted-foreground/70 truncate">
                              {item?.description}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {isActivePath(item?.path) && (
                        <div className="w-1 h-1 bg-primary-foreground rounded-full ml-2"></div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>

        {/* Quick Actions */}
        {shouldShowExpanded && (
          <div className="p-4 border-t border-border">
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Quick Actions
              </h4>
              {quickActions?.map((action, index) => (
                <Button
                  key={action?.name}
                  variant={action?.variant}
                  size="sm"
                  fullWidth
                  onClick={action?.action}
                  iconName={action?.icon}
                  iconPosition="left"
                  iconSize={16}
                >
                  {action?.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Collapsed Quick Actions */}
        {!shouldShowExpanded && (
          <div className="p-2 border-t border-border">
            <div className="space-y-2">
              {quickActions?.map((action, index) => (
                <button
                  key={action?.name}
                  onClick={action?.action}
                  className="w-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-all duration-200 flex items-center justify-center"
                  title={action?.name}
                >
                  <Icon name={action?.icon} size={18} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Status Indicator */}
        <div className="p-4 border-t border-border">
          {shouldShowExpanded ? (
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span>System Online</span>
              <div className="flex-1"></div>
              <span>v2.1.0</span>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" title="System Online"></div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;